import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  Users, 
  CheckSquare, 
  MessageSquare, 
  Smartphone, 
  Shield, 
  BarChart3,
  ArrowRight,
  Clock,
  MapPin,
  Calendar,
  Target,
  Play
} from 'lucide-react'

interface Feature {
  id: string
  title: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
  highlights: string[]
  mockImage: React.ReactNode
}

export const WorkForceFeatures: React.FC = () => {
  const { t } = useTranslation('workforce')
  
  const mockTasks = {
    room: t('featuresShowcase.tasks.mockData.room', 'Limpeza Quarto 205'),
    checkin: t('featuresShowcase.tasks.mockData.checkin', 'Check-in Família Silva'),
    inventory: t('featuresShowcase.tasks.mockData.inventory', 'Inventário Bar'),
    estimated: t('featuresShowcase.tasks.mockData.estimated', 'Estimado'),
    real: t('featuresShowcase.tasks.mockData.real', 'Real'),
    inProgress: t('featuresShowcase.tasks.mockData.inProgress', 'Em andamento'),
    completed: t('featuresShowcase.tasks.mockData.completed', 'Concluído')
  }

  const socialMockData = {
    shared: t('featuresShowcase.social.mockData.shared', 'Compartilhou um vídeo'),
    likes: t('featuresShowcase.social.mockData.likes', '👍 12'),
    comments: t('featuresShowcase.social.mockData.comments', '💬 3'),
    share: t('featuresShowcase.social.mockData.share', '📤 Compartilhar')
  }

  const features: Feature[] = [
  {
    id: 'workforce',
    title: t('features.taskManagement.title'),
    description: t('features.taskManagement.description'),
    icon: Users,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    highlights: [
      t('featuresShowcase.workforce.highlights.0'),
      t('featuresShowcase.workforce.highlights.1'),
      t('featuresShowcase.workforce.highlights.2'),
      t('featuresShowcase.workforce.highlights.3')
    ],
    mockImage: (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span className="font-medium text-gray-900">{t('featuresShowcase.workforce.weeklyPlanning')}</span>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-3">
          {['S', 'T', 'Q', 'Q', 'S', 'S', 'D'].map((day, i) => (
            <div key={i} className="text-xs text-center py-1 bg-blue-50 rounded">{day}</div>
          ))}
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span>Sara Ortolenco</span>
            <span className="bg-green-100 text-green-700 px-2 py-1 rounded">08:00-16:00</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>André Português</span>
            <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">16:00-00:00</span>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'tasks',
    title: t('featuresShowcase.tasks.title'),
    description: t('featuresShowcase.tasks.description'),
    icon: CheckSquare,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    highlights: [
      t('featuresShowcase.tasks.highlights.0'),
      t('featuresShowcase.tasks.highlights.1'),
      t('featuresShowcase.tasks.highlights.2'),
      t('featuresShowcase.tasks.highlights.3')
    ],
    mockImage: (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-green-600" />
          <span className="font-medium text-gray-900">{t('featuresShowcase.tasks.activeTasks')}</span>
        </div>
        <div className="space-y-2">
          <div className="p-2 bg-yellow-50 border-l-2 border-yellow-400 rounded">
            <div className="text-xs font-medium">{mockTasks.room}</div>
            <div className="text-xs text-gray-500">{mockTasks.estimated}: 45min • {mockTasks.real}: 32min</div>
          </div>
          <div className="p-2 bg-blue-50 border-l-2 border-blue-400 rounded">
            <div className="text-xs font-medium">{mockTasks.checkin}</div>
            <div className="text-xs text-gray-500">{mockTasks.estimated}: 15min • {mockTasks.inProgress}</div>
          </div>
          <div className="p-2 bg-green-50 border-l-2 border-green-400 rounded">
            <div className="text-xs font-medium">{mockTasks.inventory}</div>
            <div className="text-xs text-gray-500">{mockTasks.completed} • 1h 20min</div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'social',
    title: t('featuresShowcase.social.title'),
    description: t('featuresShowcase.social.description'),
    icon: MessageSquare,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    highlights: [
      t('featuresShowcase.social.highlights.0'),
      t('featuresShowcase.social.highlights.1'),
      t('featuresShowcase.social.highlights.2'),
      t('featuresShowcase.social.highlights.3')
    ],
    mockImage: (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4 text-purple-600" />
          <span className="font-medium text-gray-900">{t('featuresShowcase.social.corporateFeed')}</span>
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-xs text-white font-bold">JG</div>
            <div>
              <div className="text-xs font-medium">José Gregório</div>
              <div className="text-xs text-gray-500">{socialMockData.shared}</div>
            </div>
          </div>
          <div className="bg-gray-900 rounded aspect-video flex items-center justify-center">
            <Play className="w-6 h-6 text-white" />
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>{socialMockData.likes}</span>
            <span>{socialMockData.comments}</span>
            <span>{socialMockData.share}</span>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'mobile',
    title: t('featuresShowcase.mobile.title'),
    description: t('featuresShowcase.mobile.description'),
    icon: Smartphone,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    highlights: [
      t('featuresShowcase.mobile.highlights.0'),
      t('featuresShowcase.mobile.highlights.1'),
      t('featuresShowcase.mobile.highlights.2'),
      t('featuresShowcase.mobile.highlights.3')
    ],
    mockImage: (
      <div className="bg-gray-900 rounded-2xl p-2 mx-auto w-32">
        <div className="bg-white rounded-xl p-3">
          <div className="text-center mb-3">
            <div className="w-8 h-8 bg-orange-600 rounded-lg flex items-center justify-center mx-auto mb-2">
              <Clock className="w-4 h-4 text-white" />
            </div>
            <div className="text-xs font-bold">08:47:23</div>
            <div className="text-xs text-gray-500">{t('featuresShowcase.mobile.working')}</div>
          </div>
          <div className="space-y-1">
            <div className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs text-center">
              {t('featuresShowcase.mobile.checkIn')}: 08:00
            </div>
            <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs text-center">
              3 {t('featuresShowcase.mobile.activeTasks')}
            </div>
            <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs text-center flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" />
              {t('featuresShowcase.mobile.located')}
            </div>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'auth',
    title: t('featuresShowcase.auth.title'),
    description: t('featuresShowcase.auth.description'),
    icon: Shield,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    highlights: [
      t('featuresShowcase.auth.highlights.0'),
      t('featuresShowcase.auth.highlights.1'),
      t('featuresShowcase.auth.highlights.2'),
      t('featuresShowcase.auth.highlights.3')
    ],
    mockImage: (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="text-center mb-4">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center mx-auto mb-2">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div className="text-sm font-medium">{t('featuresShowcase.auth.secureLogin')}</div>
        </div>
        <div className="space-y-2">
          <button className="w-full bg-white border border-gray-300 px-3 py-2 rounded text-xs flex items-center justify-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            {t('featuresShowcase.auth.continueWithGoogle')}
          </button>
          <div className="text-xs text-center text-gray-500">{t('featuresShowcase.auth.or')}</div>
          <div className="space-y-1">
            <input className="w-full border border-gray-300 px-2 py-1 rounded text-xs" placeholder="email@empresa.com" />
            <input className="w-full border border-gray-300 px-2 py-1 rounded text-xs" placeholder="••••••••" />
          </div>
          <button className="w-full bg-indigo-600 text-white px-3 py-1 rounded text-xs">
            {t('featuresShowcase.auth.login')}
          </button>
          <div className="text-xs text-center">
            <a href="#" className="text-indigo-600">{t('featuresShowcase.auth.forgotPassword')}</a>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 'analytics',
    title: t('featuresShowcase.analytics.title'),
    description: t('featuresShowcase.analytics.description'),
    icon: BarChart3,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    highlights: [
      t('featuresShowcase.analytics.highlights.0'),
      t('featuresShowcase.analytics.highlights.1'),
      t('featuresShowcase.analytics.highlights.2'),
      t('featuresShowcase.analytics.highlights.3')
    ],
    mockImage: (
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-cyan-600" />
          <span className="font-medium text-gray-900">{t('featuresShowcase.analytics.department', 'Recepção')}</span>
        </div>
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600">{t('featuresShowcase.analytics.satisfaction')}</span>
            <span className="text-xs font-bold text-cyan-600">85%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-cyan-600 h-2 rounded-full" style={{width: '85%'}}></div>
          </div>
          <div className="text-xs text-gray-500 mt-1">{t('featuresShowcase.analytics.good')} • {t('featuresShowcase.analytics.aboveAverage')}</div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="text-center">
            <div className="font-bold text-gray-900">2</div>
            <div className="text-gray-500">{t('featuresShowcase.analytics.online')}</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-gray-900">1</div>
            <div className="text-gray-500">{t('featuresShowcase.analytics.schedules')}</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-gray-900">0%</div>
            <div className="text-gray-500">{t('featuresShowcase.analytics.tasks')}</div>
          </div>
        </div>
      </div>
    )
  }
]
  
  return (
    <section className="py-24 bg-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-900 mb-4"
          >
            {t('features.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('features.subtitle')}
          </motion.p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature, index) => (
            <Link 
              key={feature.id}
              to={
                feature.id === 'tasks' ? `/task-management/feature/task-management` :
                feature.id === 'social' ? `/social-enterprise/feature/social` :
                feature.id === 'mobile' ? `/mobile-workforce/feature/mobile` :
                feature.id === 'analytics' ? `/analytics-departamentais/feature/analytics` :
                feature.id === 'auth' ? `/authentication/feature/auth` :
                `/workforce/feature/${feature.id}`
              }
              className="block"
            >
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl cursor-pointer transition-all duration-300 hover:-translate-y-2 group h-full"
              >
                <div className={`w-14 h-14 ${feature.bgColor} rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon className={`w-8 h-8 ${feature.color}`} />
                </div>
                
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">{feature.description}</p>
                
                <div className="flex items-center text-orange-600 text-sm font-medium group-hover:text-orange-700 transition-colors">
                  <span>{t('featuresShowcase.learnMore')}</span>
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-gray-600 mb-6">
            {t('featuresShowcase.bottomText')}
          </p>
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 inline-flex items-center gap-2"
            >
              {t('featuresShowcase.scheduleDemo')}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}